import {
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import ApexCharts, { type ApexOptions } from 'apexcharts';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import {
  DemograficoEstatisticaSic,
  MesEstatisticaSic,
} from '../../models/estatistica-sic-publica.model';
import { EstatisticaSicPublicaService } from '../../services/estatistica-sic-publica.service';

const MESES: readonly string[] = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/** A mesma mensagem do validator do back-end: a tela recusa antes de pedir. */
export const MENSAGEM_PERIODO_INVERTIDO = 'A data final deve ser igual ou posterior à data inicial.';

const CORES_FAIXA_ETARIA: readonly string[] = ['#8e44ad', '#2980b9', '#f39c12'];
const CORES_GENERO: readonly string[] = ['#c0392b', '#d35400', '#f39c12'];
const CORES_DOMICILIO: readonly string[] = [
  '#2e00ff',
  '#79d6ff',
  '#8e44ad',
  '#f39c12',
  '#16a085',
  '#c0392b',
];

/**
 * A página pública *Estatísticas e-SIC* (CDS-40). **Nenhum número nasce aqui**: KPIs, tendência,
 * respostas, motivos, perfil e demográficos vêm da leitura pública da API, que agrega no servidor —
 * inclusive faixa etária, bairro e gênero, com o piso de supressão. A tela só calcula percentual e
 * fração, que são exibição.
 */
@Component({
  selector: 'app-estatisticas-sic',
  templateUrl: './estatisticas-sic.component.html',
  styleUrls: ['./estatisticas-sic.component.css'],
  imports: [BreadcrumbComponent, FormsModule, LucideModule, NgTemplateOutlet],
  providers: [EstatisticaSicPublicaService],
})
export class EstatisticasSicComponent implements OnDestroy {
  private readonly servico = inject(EstatisticaSicPublicaService);

  readonly trilha = [{ titulo: 'SIC', link: '/sic' }];

  readonly dataInicio = signal('');
  readonly dataFim = signal('');

  /** A recusa do período invertido, exibida junto do filtro; a leitura anterior continua na tela. */
  readonly erroFiltro = signal<string | null>(null);

  readonly carregando = this.servico.carregando;
  readonly erro = this.servico.erro;
  readonly dados = this.servico.estatisticas;

  private readonly elTendenciaMensal = viewChild<ElementRef<HTMLElement>>('graficoTendenciaMensal');
  private readonly elFaixaEtaria = viewChild<ElementRef<HTMLElement>>('graficoFaixaEtaria');
  private readonly elDomicilio = viewChild<ElementRef<HTMLElement>>('graficoDomicilio');
  private readonly elGenero = viewChild<ElementRef<HTMLElement>>('graficoGenero');

  private chartTendenciaMensal: ApexCharts | null = null;
  private chartFaixaEtaria: ApexCharts | null = null;
  private chartDomicilio: ApexCharts | null = null;
  private chartGenero: ApexCharts | null = null;

  /** O período devolvido pela API — o aplicado, inclusive quando a página abre sem filtro. */
  readonly periodo = computed(() => {
    const dados = this.dados();

    return dados ? `${this.formatarData(dados.dataInicial)} a ${this.formatarData(dados.dataFinal)}` : '';
  });

  readonly kpis = computed(() => this.dados()?.kpis);
  readonly recebidos = computed(() => this.kpis()?.recebidos ?? 0);

  readonly percentualAtendidos = computed(() => this.percentual(this.kpis()?.atendidos ?? 0, this.recebidos()));
  readonly percentualParcial = computed(() =>
    this.percentual(this.kpis()?.parcialmenteAtendidos ?? 0, this.recebidos())
  );
  readonly percentualIndeferidos = computed(() => this.percentual(this.kpis()?.indeferidos ?? 0, this.recebidos()));
  readonly percentualNaoRespondidos = computed(() =>
    this.percentual(this.kpis()?.naoRespondidos ?? 0, this.recebidos())
  );

  constructor() {
    effect(() => {
      this.chartTendenciaMensal = this.renderizarTendenciaMensal(
        this.elTendenciaMensal()?.nativeElement,
        this.dados()?.tendenciaMensal
      );
    });

    effect(() => {
      this.chartFaixaEtaria = this.renderizarDemografico(
        this.elFaixaEtaria()?.nativeElement,
        this.chartFaixaEtaria,
        this.dados()?.faixaEtaria,
        CORES_FAIXA_ETARIA
      );
    });

    effect(() => {
      this.chartDomicilio = this.renderizarDemografico(
        this.elDomicilio()?.nativeElement,
        this.chartDomicilio,
        this.dados()?.domicilio,
        CORES_DOMICILIO
      );
    });

    effect(() => {
      this.chartGenero = this.renderizarDemografico(
        this.elGenero()?.nativeElement,
        this.chartGenero,
        this.dados()?.genero,
        CORES_GENERO
      );
    });
  }

  ngOnDestroy(): void {
    this.chartTendenciaMensal?.destroy();
    this.chartFaixaEtaria?.destroy();
    this.chartDomicilio?.destroy();
    this.chartGenero?.destroy();
  }

  /** Barra proporcional ao total de pedidos recebidos no período. */
  getPercentual(valor: number): number {
    return this.percentual(valor, this.recebidos());
  }

  /** Barra proporcional ao total do perfil — todos os pedidos do período. */
  getPercentualPerfil(valor: number): number {
    return this.percentual(valor, this.dados()?.perfilSolicitantes.total ?? 0);
  }

  /**
   * Datas invertidas são recusadas **aqui**, com a mesma mensagem do servidor, sem disparar a
   * requisição: os números da leitura anterior continuam exibidos.
   */
  aplicarFiltros(): void {
    const dataInicial = this.dataInicio();
    const dataFinal = this.dataFim();

    // `yyyy-MM-dd` compara na ordem cronológica.
    if (dataInicial && dataFinal && dataFinal < dataInicial) {
      this.erroFiltro.set(MENSAGEM_PERIODO_INVERTIDO);
      return;
    }

    this.erroFiltro.set(null);
    this.servico.filtrar({ dataInicial: dataInicial || undefined, dataFinal: dataFinal || undefined });
  }

  limparFiltros(): void {
    this.dataInicio.set('');
    this.dataFim.set('');
    this.erroFiltro.set(null);

    this.servico.filtrar({});
  }

  private percentual(parte: number, total: number): number {
    if (total <= 0) return 0;

    return parseFloat(((parte / total) * 100).toFixed(2));
  }

  /** `2026-01-01T00:00:00` → `01/01/2026`, sem passar por `Date` (nenhum fuso desloca o dia). */
  private formatarData(iso: string): string {
    const [ano, mes, dia] = iso.substring(0, 10).split('-');

    return `${dia}/${mes}/${ano}`;
  }

  /** Só o mês quando o período cabe num ano; `Mar/2026` quando cruza anos. */
  private rotulosMeses(meses: readonly MesEstatisticaSic[]): string[] {
    const cruzaAnos = meses.length > 0 && meses[0].ano !== meses[meses.length - 1].ano;

    return meses.map((item) =>
      cruzaAnos ? `${MESES[item.mes - 1].substring(0, 3)}/${item.ano}` : MESES[item.mes - 1]
    );
  }

  private renderizarTendenciaMensal(
    elemento: HTMLElement | undefined,
    meses: readonly MesEstatisticaSic[] | undefined
  ): ApexCharts | null {
    this.chartTendenciaMensal?.destroy();

    if (!elemento || !meses) return null;

    const options: ApexOptions = {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: { curve: 'smooth', width: 2 },
      markers: {
        size: 5,
        strokeWidth: 2,
        strokeColors: ['#928FDB', '#C0E4CE', '#FFD484'],
        colors: ['#ffffff', '#ffffff', '#ffffff'],
        fillOpacity: 1,
        shape: 'circle',
        hover: { sizeOffset: 3 },
      },
      series: [
        { name: 'Recebidas', data: meses.map((item) => item.recebidas) },
        { name: 'Respondidas', data: meses.map((item) => item.respondidas) },
        { name: 'No prazo', data: meses.map((item) => item.noPrazo) },
      ],
      colors: ['#928FDB', '#C0E4CE', '#FFD484'],
      xaxis: {
        categories: this.rotulosMeses(meses),
        labels: { style: { fontSize: '13px', colors: '#2c3e50' } },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          formatter: (val: number) => `${Math.round(val)}`,
          style: { fontSize: '12px', colors: '#7f8c8d' },
        },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'left',
        fontSize: '14px',
        fontWeight: 500,
        markers: { size: 6, shape: 'circle' },
        itemMargin: { horizontal: 10, vertical: 5 },
        labels: { colors: '#2c3e50' },
      },
      tooltip: {
        y: { formatter: (val: number) => `${val} pedidos` },
      },
      grid: { borderColor: '#ecf0f1', strokeDashArray: 5 },
    };

    const chart = new ApexCharts(elemento, options);
    chart.render();

    return chart;
  }

  /**
   * Os três demográficos: as fatias como a API as devolve. Não publicado não chega aqui — o
   * template troca o gráfico pelo aviso de dados insuficientes, e o elemento some.
   */
  private renderizarDemografico(
    elemento: HTMLElement | undefined,
    anterior: ApexCharts | null,
    demografico: DemograficoEstatisticaSic | undefined,
    cores: readonly string[]
  ): ApexCharts | null {
    anterior?.destroy();

    if (!elemento || !demografico?.publicado) return null;

    const rotulos = demografico.fatias.map((fatia) => fatia.rotulo);

    const options: ApexOptions = {
      chart: { type: 'polarArea', height: 400 },
      series: demografico.fatias.map((fatia) => fatia.quantidade),
      labels: rotulos,
      colors: rotulos.map((_, indice) => cores[indice % cores.length]),
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const porcentagem = Math.round(Number(val) * 100) / 100;
          const rotulo = opts ? rotulos[opts.seriesIndex] : '';

          return rotulo ? `${rotulo}: ${porcentagem}%` : `${porcentagem}%`;
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
      },
    };

    const chart = new ApexCharts(elemento, options);
    chart.render();

    return chart;
  }
}
